/**
 * Small, fast, seedable PRNG (mulberry32).
 *
 * The daily shift is seeded from the calendar date so every player gets the
 * same night (same hurricane, same fight, same weird customers), which is what
 * makes the share card comparable — Wordle-style.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, max] (inclusive). */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  chance(probability: number): boolean {
    return this.next() < probability;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick called with an empty array');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Pick from `[value, weight]` pairs. Non-positive weights are never picked. */
  weighted<T>(entries: readonly (readonly [T, number])[]): T {
    let total = 0;
    for (const [, w] of entries) total += Math.max(0, w);
    if (total <= 0) throw new Error('Rng.weighted needs at least one positive weight');
    let roll = this.next() * total;
    for (const [value, w] of entries) {
      if (w <= 0) continue;
      roll -= w;
      if (roll < 0) return value;
    }
    // Floating point fallthrough: return the last positive entry.
    for (let i = entries.length - 1; i >= 0; i--) {
      const entry = entries[i];
      if (entry && entry[1] > 0) return entry[0];
    }
    throw new Error('unreachable');
  }

  /** Fisher–Yates shuffle, in place. Returns the same array for chaining. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = items[i] as T;
      items[i] = items[j] as T;
      items[j] = tmp;
    }
    return items;
  }

  /** Derive an independent stream so unrelated systems don't perturb each other. */
  fork(salt: string): Rng {
    return new Rng(hashString(`${this.state}:${salt}`));
  }
}

/** FNV-1a 32-bit string hash. */
export function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
